/**
 * Estado de colaboración de un peritaje.
 *
 * Es un store mínimo y observable (sin dependencias) para que tanto el cliente
 * WebSocket como `back.service.js` puedan leer y modificar el mismo estado sin
 * importarse mutuamente.
 */
import { INITIAL_VERSION } from './scopes';

const state = {
  connected: false,
  inspectionId: null,
  presence: [],
  locks: {},
  versions: {},
  // Formularios con cambios sin guardar en esta sesión.
  dirty: {},
  // Scope -> motivo del rechazo del servidor por versión obsoleta.
  conflicts: {},
  // Scope -> perito al que el servidor le negó el lock.
  denied: {},
  // Scope -> contador de guardados remotos, para refrescar el formulario.
  remoteSaves: {},
};

const listeners = new Set();

export function getState() {
  return state;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  listeners.forEach((listener) => listener(state));
}

/** Id del perito conectado, persistido al iniciar sesión. */
export function currentUserId() {
  const raw = localStorage.getItem('user_id');
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export function reset() {
  state.connected = false;
  state.inspectionId = null;
  state.presence = [];
  state.locks = {};
  state.versions = {};
  state.dirty = {};
  state.conflicts = {};
  state.denied = {};
  state.remoteSaves = {};
  emit();
}

export function setConnected(connected) {
  if (state.connected === connected) return;
  state.connected = connected;
  emit();
}

export function setInspectionId(inspectionId) {
  if (state.inspectionId === inspectionId) return;
  state.inspectionId = inspectionId;
  emit();
}

export function setPresence(presence) {
  state.presence = Array.isArray(presence) ? presence : [];
  emit();
}

export function applySnapshot(data) {
  if (!data) return;
  state.presence = Array.isArray(data.presence) ? data.presence : [];
  state.versions = { ...state.versions, ...(data.versions || {}) };
  state.locks = {};
  (data.locks || []).forEach((lock) => {
    if (lock.holder_id) state.locks[lock.scope] = lock;
  });
  emit();
}

/** Aplica un evento de lock: holder null significa "formulario libre". */
export function applyLock(payload) {
  if (!payload || !payload.scope) return;
  const { scope, holder, holder_id: holderId, version } = payload;

  if (version !== undefined && version !== null) {
    state.versions[scope] = version;
  }

  if (holderId) {
    state.locks[scope] = { scope, holder, holder_id: holderId, since: payload.since };
  } else {
    delete state.locks[scope];
  }

  const me = currentUserId();
  const isMine = Boolean(holderId) && (me === null ? true : holderId === me);
  // Un formulario libre, o que ya es mío, siempre deja de estar denegado.
  if (isMine || !holderId) delete state.denied[scope];

  emit();
}

export function setDenied(scope, holder) {
  if (!scope) return;
  state.denied[scope] = holder || true;
  emit();
}

/** Olvida un lock localmente (el servidor lo liberó o venció). */
export function dropLocalLock(scope) {
  if (!scope || !state.locks[scope]) return;
  delete state.locks[scope];
  emit();
}

export function clearDenied(scope) {
  if (scope && scope in state.denied) {
    delete state.denied[scope];
    emit();
  }
}

/** Marca el formulario como "con cambios sin guardar" → pide el lock. */
export function markDirty(scope) {
  if (!scope || state.dirty[scope]) return;
  state.dirty[scope] = true;
  delete state.conflicts[scope];
  emit();
}

/** El formulario se guardó o se descartó → suelta el lock. */
export function markClean(scope) {
  if (!scope || !state.dirty[scope]) return;
  delete state.dirty[scope];
  emit();
}

export function isDirty(scope) {
  return Boolean(state.dirty[scope]);
}

export function dirtyScopes() {
  return Object.keys(state.dirty);
}

export function setVersion(scope, version) {
  if (!scope || version === undefined || version === null) return;
  if (state.versions[scope] === version) return;
  state.versions[scope] = version;
  emit();
}

export function versionFor(scope) {
  const known = state.versions[scope];
  return known === undefined ? INITIAL_VERSION : known;
}

/**
 * Versión solo si el cliente ya la conoce del servidor.
 *
 * Al abrir un peritaje todavía no llegó el snapshot: exigir una versión que
 * nunca vimos daría conflictos falsos, así que el primer guardado viaja sin
 * versión y a partir de ahí ya se valida cada uno.
 */
export function versionIfKnown(scope) {
  return state.versions[scope];
}

export function setConflict(scope, payload) {
  if (!scope) return;
  state.conflicts[scope] = {
    message:
      (payload && payload.message) ||
      'Este formulario cambió mientras lo editabas.',
    serverVersion: payload ? payload.server_version : undefined,
  };
  emit();
}

export function clearConflict(scope) {
  if (scope && scope in state.conflicts) {
    delete state.conflicts[scope];
    emit();
  }
}

/** Registra un guardado hecho por otro perito (para refrescar el formulario). */
export function noteRemoteSave(scope) {
  if (!scope) return;
  state.remoteSaves[scope] = (state.remoteSaves[scope] || 0) + 1;
  emit();
}

export function remoteSaveCount(scope) {
  return state.remoteSaves[scope] || 0;
}

/** Descarta los cambios locales del formulario (recarga / vuelve atrás). */
export function discardChanges(scope) {
  if (!scope) return;
  const changed = Boolean(state.dirty[scope]) || scope in state.conflicts;
  delete state.dirty[scope];
  delete state.conflicts[scope];
  if (changed) emit();
}

export function lockOf(scope) {
  return state.locks[scope] || null;
}

export function holderName(scope) {
  const lock = state.locks[scope];
  if (lock && lock.holder && lock.holder.name) return lock.holder.name;
  if (state.denied[scope] && state.denied[scope].name) return state.denied[scope].name;
  return null;
}

export function isDenied(scope) {
  return Boolean(state.denied[scope]);
}

/** ¿El lock lo tiene el perito conectado? */
export function holdsLock(scope) {
  const lock = state.locks[scope];
  if (!lock || !lock.holder_id) return false;
  const me = currentUserId();
  if (me === null) return isDirty(scope);
  return lock.holder_id === me;
}

/** ¿Otro perito tiene tomado este formulario? */
export function isHeldByOther(scope) {
  const lock = state.locks[scope];
  if (!lock || !lock.holder_id) return false;
  const me = currentUserId();
  if (me === null) return !isDirty(scope);
  return lock.holder_id !== me;
}